import React from "react";
import {
  AiSummaryNutrition,
  AiSummaryNutritionDay,
  AiSummaryRecommendationItem,
  AiSummaryRecommendationKey,
  AiSummaryRecommendationValue,
  AiSummaryRecommendations,
  AiSummaryTrainingDay,
} from "./aiSummaryTypes";
import classes from "./AiSummaryRecommendationsView.module.css";

type Props = {
  trainingPlan: AiSummaryTrainingDay[];
  nutrition: AiSummaryNutrition;
  recommendations: AiSummaryRecommendations;
  variant?: "screen" | "pdf";
};

type RecommendationItem = AiSummaryRecommendationItem;

const SERVICE_CONFIG: Array<{
  key: AiSummaryRecommendationKey;
  label: string;
  mark: string;
  toneClass: string;
}> = [
  {
    key: "longevity_treatments",
    label: "Longevity treatments",
    mark: "LT",
    toneClass: classes.serviceLongevity,
  },
  {
    key: "supplements",
    label: "Supplements",
    mark: "SUP",
    toneClass: classes.serviceSupplements,
  },
  { key: "ivs", label: "IVs", mark: "IV", toneClass: classes.serviceIvs },
  {
    key: "pharmaceuticals",
    label: "Pharmaceuticals",
    mark: "RX",
    toneClass: classes.servicePharmaceuticals,
  },
  {
    key: "pharmaceutical_ivs",
    label: "Pharmaceutical IVs",
    mark: "RX+",
    toneClass: classes.servicePharmaceuticalIvs,
  },
];

const GUIDANCE_CONFIG: Array<{
  key: "exercise" | "nutrition" | "lifestyle";
  label: string;
}> = [
  { key: "exercise", label: "Training guidance" },
  { key: "nutrition", label: "Nutrition guidance" },
  { key: "lifestyle", label: "Lifestyle" },
];

const formatNumber = (value: number): string =>
  new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Number.isInteger(value) ? 0 : 2,
  }).format(value);

const hasRecommendationContent = (
  value: AiSummaryRecommendationValue | undefined
): boolean =>
  Array.isArray(value) ? value.length > 0 : Boolean(value?.trim());

const isStructuredRecommendation = (
  value: AiSummaryRecommendationValue
): value is AiSummaryRecommendationItem[] =>
  Array.isArray(value) &&
  value.every((item) => typeof item === "object" && item !== null);

const toRecommendationItems = (
  value: AiSummaryRecommendationValue
): RecommendationItem[] => {
  if (typeof value === "string") {
    return [{ title: "", description: value, details: [] }];
  }

  if (isStructuredRecommendation(value)) {
    return value;
  }

  return value.map((description) => ({
    title: "",
    description,
    details: [],
  }));
};

const getTrainingTone = (activity: string): string => {
  const normalized = activity.toLowerCase();
  if (normalized.includes("rest")) {
    return classes.trainingRest;
  }
  if (normalized.includes("hiit") || normalized.includes("interval")) {
    return classes.trainingInterval;
  }
  if (normalized.includes("strength") || normalized.includes("resistance")) {
    return classes.trainingStrength;
  }
  if (normalized.includes("cardio") || normalized.includes("endurance")) {
    return classes.trainingCardio;
  }
  if (normalized.includes("yoga")) {
    return classes.trainingYoga;
  }
  if (normalized.includes("pilates")) {
    return classes.trainingPilates;
  }
  return classes.trainingGeneral;
};

const NutritionDayCard = ({
  title,
  day,
}: {
  title: string;
  day: AiSummaryNutritionDay;
}) => {
  const macros = [
    {
      key: "protein",
      label: "Protein",
      value: day.proteinGrams,
      caloriesPerGram: 4,
      className: classes.macroProtein,
      dotClassName: classes.proteinDot,
    },
    {
      key: "carbs",
      label: "Carbs",
      value: day.carbsGrams,
      caloriesPerGram: 4,
      className: classes.macroCarbs,
      dotClassName: classes.carbsDot,
    },
    {
      key: "fat",
      label: "Fats",
      value: day.fatGrams,
      caloriesPerGram: 9,
      className: classes.macroFat,
      dotClassName: classes.fatDot,
    },
  ].filter((macro) => macro.value !== undefined && macro.value >= 0);
  const totalMacroCalories = macros.reduce(
    (total, macro) => total + (macro.value ?? 0) * macro.caloriesPerGram,
    0
  );

  return (
    <article className={classes.nutritionCard} data-pdf-break="after">
      <h4>{title}</h4>
      {(day.caloricBurnKcal !== undefined ||
        day.caloricIntakeKcal !== undefined) && (
        <div className={classes.energyGrid}>
          {day.caloricBurnKcal !== undefined && (
            <div>
              <span>You burn</span>
              <strong>{formatNumber(day.caloricBurnKcal)}</strong>
              <small>kcal</small>
            </div>
          )}
          {day.caloricIntakeKcal !== undefined && (
            <div>
              <span>You should eat</span>
              <strong>{formatNumber(day.caloricIntakeKcal)}</strong>
              <small>kcal</small>
            </div>
          )}
        </div>
      )}
      {totalMacroCalories > 0 && (
        <div
          className={classes.macroBar}
          aria-label={`${title} macronutrients`}
        >
          {macros.map((macro) => (
            <span
              key={macro.key}
              className={macro.className}
              style={{
                width: `${
                  (((macro.value ?? 0) * macro.caloriesPerGram) /
                    totalMacroCalories) *
                  100
                }%`,
              }}
            />
          ))}
        </div>
      )}
      {macros.length > 0 && (
        <div className={classes.macroLegend}>
          {macros.map((macro) => (
            <span key={macro.key}>
              <i className={macro.dotClassName} /> {macro.label}{" "}
              {formatNumber(macro.value ?? 0)} g
            </span>
          ))}
        </div>
      )}
    </article>
  );
};

const RecommendationItems = ({ items }: { items: RecommendationItem[] }) => (
  <ul className={classes.serviceItems}>
    {items.map((item, index) => {
      return (
        <li key={`${item.title}-${item.description}-${index}`}>
          <div className={classes.itemContent}>
            {item.title && <strong>{item.title}</strong>}
            {item.description && (
              <p className={classes.itemDescription}>{item.description}</p>
            )}
            {item.details.length > 0 && (
              <div className={classes.itemDetails}>
                {item.details.map((detail, detailIndex) => (
                  <span key={`${detail.label}-${detail.value}-${detailIndex}`}>
                    {detail.label && <small>{detail.label}</small>}
                    <b>{detail.value}</b>
                  </span>
                ))}
              </div>
            )}
          </div>
        </li>
      );
    })}
  </ul>
);

const ServiceRecommendationCard = ({
  category,
  mark,
  toneClass,
  item,
  index,
}: {
  category: string;
  mark: string;
  toneClass: string;
  item: RecommendationItem;
  index: number;
}) => {
  const accessibleName = `${category}: ${
    item.title || item.description || `recommendation ${index + 1}`
  }`;

  return (
    <article
      className={`${classes.serviceTile} ${toneClass}`}
      aria-label={accessibleName}
      data-pdf-break="after"
    >
      <header className={classes.serviceTileHeader}>
        <span className={classes.serviceMark} aria-hidden="true">
          {mark}
        </span>
        <span className={classes.serviceCategory}>{category}</span>
      </header>
      <div className={classes.serviceTileBody}>
        {item.title && <h4>{item.title}</h4>}
        {item.description && (
          <p className={classes.serviceDescription}>{item.description}</p>
        )}
      </div>
      {item.details.length > 0 && (
        <dl className={classes.serviceDetailGrid}>
          {item.details.map((detail, detailIndex) => (
            <div key={`${detail.label}-${detail.value}-${detailIndex}`}>
              {detail.label && <dt>{detail.label}</dt>}
              <dd>{detail.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </article>
  );
};

export const hasAiSummaryRecommendations = ({
  trainingPlan,
  nutrition,
  recommendations,
}: Omit<Props, "variant">): boolean => {
  const hasNutrition = Boolean(
    nutrition.nonWorkoutDays || nutrition.workoutDays
  );
  return Boolean(
    trainingPlan.length > 0 ||
      hasNutrition ||
      SERVICE_CONFIG.some(({ key }) => recommendations[key] !== undefined) ||
      Object.values(recommendations).some(hasRecommendationContent)
  );
};

const AiSummaryRecommendationsView = ({
  trainingPlan,
  nutrition,
  recommendations,
  variant = "screen",
}: Props) => {
  const hasNutrition = Boolean(
    nutrition.nonWorkoutDays || nutrition.workoutDays
  );
  const services = SERVICE_CONFIG.flatMap((config) => {
    const value = recommendations[config.key];
    return value && hasRecommendationContent(value)
      ? [{ ...config, items: toRecommendationItems(value) }]
      : [];
  });
  const hasServicesResponse = SERVICE_CONFIG.some(
    ({ key }) => recommendations[key] !== undefined
  );
  const guidance = GUIDANCE_CONFIG.flatMap((config) => {
    const isRepresentedByWidget =
      (config.key === "exercise" && trainingPlan.length > 0) ||
      (config.key === "nutrition" && hasNutrition);
    const value = recommendations[config.key];
    return !isRepresentedByWidget && value && hasRecommendationContent(value)
      ? [{ ...config, items: toRecommendationItems(value) }]
      : [];
  });

  if (
    !hasAiSummaryRecommendations({ trainingPlan, nutrition, recommendations })
  ) {
    return null;
  }

  return (
    <div
      className={`${classes.root} ${
        variant === "pdf" ? classes.pdf : classes.screen
      }`}
    >
      {trainingPlan.length > 0 && (
        <section
          className={classes.recommendationSection}
          data-pdf-break-before="true"
        >
          <h3 className={classes.sectionTitle}>Training plan</h3>
          <div className={classes.trainingGrid} data-pdf-break="after">
            {trainingPlan.map((day, index) => {
              const toneClass = getTrainingTone(day.activity);
              const isRest = toneClass === classes.trainingRest;
              return (
                <article
                  key={`${day.weekday}-${index}`}
                  className={`${classes.trainingDay} ${toneClass}`}
                >
                  <span>{day.weekday}</span>
                  <strong>{day.activity}</strong>
                  {!isRest && day.subtype && <i>{day.subtype}</i>}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {hasNutrition && (
        <section
          className={classes.recommendationSection}
          data-pdf-break-before="true"
        >
          <h3 className={classes.sectionTitle}>
            Energy intake &amp; macronutrient targets
          </h3>
          <div className={classes.nutritionGrid}>
            {nutrition.nonWorkoutDays && (
              <NutritionDayCard
                title="Non-workout days"
                day={nutrition.nonWorkoutDays}
              />
            )}
            {nutrition.workoutDays && (
              <NutritionDayCard
                title="Workout days"
                day={nutrition.workoutDays}
              />
            )}
          </div>
        </section>
      )}

      {hasServicesResponse && (
        <section
          className={classes.recommendationSection}
          data-pdf-break-before="true"
        >
          <h3 className={classes.sectionTitle}>Additional services</h3>
          {services.length === 0 ? (
            <p className={classes.emptyServices} data-pdf-break="after">
              No additional services were returned for this summary.
            </p>
          ) : (
            <div className={classes.servicesGrid}>
              {services.flatMap((service) =>
                service.items.map((item, index) => (
                  <ServiceRecommendationCard
                    key={`${service.key}-${item.title}-${item.description}-${index}`}
                    category={service.label}
                    mark={service.mark}
                    toneClass={service.toneClass}
                    item={item}
                    index={index}
                  />
                ))
              )}
            </div>
          )}
        </section>
      )}

      {guidance.length > 0 && (
        <section
          className={classes.recommendationSection}
          data-pdf-break-before="true"
        >
          <h3 className={classes.sectionTitle}>Additional guidance</h3>
          <div className={classes.guidanceGrid}>
            {guidance.map((item) => (
              <article
                key={item.key}
                className={classes.guidanceCard}
                data-pdf-break="after"
              >
                <h4>{item.label}</h4>
                <RecommendationItems items={item.items} />
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default AiSummaryRecommendationsView;
